import { Paper, TextField, Typography } from '@material-ui/core'

import { getTranslate } from '../../localization'
import useStyles from './styles/InventoryHealthColumnFilters.styles'
import { ColumnFilterOptions, ColumnFilters } from './useComparisonRowFilters'

type Props = {
  filters: ColumnFilters
  setFilters: (filters: ColumnFilters) => void
  options: ColumnFilterOptions
  shown: number
  total: number
}

/**
 * Narrows what is ALREADY on screen, for reading.
 *
 * Deliberately separate from the filter panel and deliberately instant: these
 * cost nothing, whereas an Apply in the panel is a PMS-backed query. Putting
 * them behind the same Apply would make the cheap act pay the expensive act's
 * price, and putting the panel's fields here would re-query on every keystroke.
 *
 * The options come from the loaded rows, so a filter can never offer a value
 * that matches nothing — an empty result from a choice the screen itself
 * offered reads as a broken screen.
 */
export default function InventoryHealthColumnFilters({
  filters,
  setFilters,
  options,
  shown,
  total
}: Props) {
  const classes = useStyles()

  const select = (
    key: keyof ColumnFilters,
    label: string,
    values: string[]
  ) => (
    <TextField
      select
      size="small"
      fullWidth
      label={getTranslate(label)}
      value={filters[key]}
      onChange={(event) =>
        setFilters({ ...filters, [key]: event.target.value })
      }
      id={`inventory-health-filter-${key}`}
      /*
        Native rather than MUI's menu. MUI v4 renders a div[role=button] that no
        label is associated with, so the control is unlabelled for a screen
        reader and unreachable by label in a test. A native select is a real
        form control, gets the platform picker on mobile, and keeps the
        keyboard behaviour users already expect.
      */
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}>
      <option value="">{getTranslate('All')}</option>
      {values.map((value) => (
        <option key={value} value={value}>
          {getTranslate(value)}
        </option>
      ))}
    </TextField>
  )

  return (
    <Paper
      className={classes.root}
      role="group"
      aria-label={getTranslate('Narrow the rows shown')}>
      {select('roomType', 'Room type', options.roomTypes)}
      {select('ratePlan', 'Rate plan', options.ratePlans)}
      {select('channelName', 'Show channel', options.channels)}
      {select('assessment', 'Assessment', options.assessments)}
      <Typography
        variant="caption"
        color="textSecondary"
        className={classes.count}>
        {getTranslate('{shown} of {total} rows', { shown, total })}
      </Typography>
    </Paper>
  )
}
